import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, CreditCard, Home, Loader2, Lock, ShoppingBag } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fcfa, ttc } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/lib/shop";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/motion";

const PAYMENT_METHODS = [
  { id: "wave", label: "Wave", desc: "Virement Wave vers notre numéro, vérifié à réception", color: "wave" },
  { id: "orange_money", label: "Orange Money", desc: "Virement Orange Money vers notre numéro, vérifié à réception", color: "orangemoney" },
  { id: "card", label: "Carte bancaire", desc: "Visa, Mastercard — sécurisé par Stripe", color: "bark" },
];

const CITIES = ["Dakar", "Thiès", "Mbour", "Saint-Louis", "Autre ville"];

const AUTRE_VILLE_OPTION = {
  id: "autre_ville",
  name: "Livraison à définir",
  delay: "Frais confirmés par la boutique après commande",
  price: 0,
  city: "Autre ville",
};

const inputClass =
  "h-14 w-full rounded-md border border-clay bg-cream px-4 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const Checkout = () => {
  useSeo({ title: "Finaliser ma commande", path: "/commander", noindex: true });
  const { items, total, clear } = useCart();
  const shop = useShop();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [options, setOptions] = useState([]);
  const [optionId, setOptionId] = useState("");
  const [method, setMethod] = useState("wave");
  const [plan, setPlan] = useState("full");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", zone: "", custom_city: "" });

  useEffect(() => {
    api.get("/delivery-zones").then((r) => setZones(r.data.zones)).catch(() => {});
    api
      .get("/delivery-options")
      .then((r) => {
        setOptions(r.data);
        if (r.data.length) setOptionId(r.data[0].id);
      })
      .catch(() => {});
  }, []);

  const zone = zones.find((z) => z.id === form.zone);
  const city = zone?.city || "Dakar";
  const isAutreVille = city === "Autre ville";
  const cityOptions = isAutreVille ? [AUTRE_VILLE_OPTION] : options.filter((o) => (o.city || "Dakar") === city);
  const selectedOption = cityOptions.find((o) => o.id === optionId);
  const deliveryFee = selectedOption ? selectedOption.price : null;
  const franceFees = items.reduce((s, i) => s + (i.shipping_fee || 0) * i.quantity, 0);
  const grandTotal = total + franceFees + (deliveryFee || 0);

  useEffect(() => {
    if (cityOptions.length && !selectedOption) setOptionId(cityOptions[0].id);
  }, [city, options]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = useMemo(
    () =>
      items.length > 0 &&
      form.name.trim() &&
      form.phone.trim() &&
      form.address.trim() &&
      form.zone &&
      (!isAutreVille || form.custom_city.trim().length >= 2) &&
      selectedOption &&
      !loading,
    [items, form, selectedOption, loading]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        payment_method: method,
        payment_plan: plan,
        delivery_option_id: optionId,
        custom_city: form.custom_city,
      });
      localStorage.setItem(
        "sk_last_order",
        JSON.stringify({
          orderNumber: order.order_number,
          orderId: order.id,
          phone: form.phone,
          method,
          total: order.total,
          paymentPlan: order.payment_plan,
          deposit: order.deposit_amount,
          remaining: order.remaining_amount,
        })
      );
      clear();
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        navigate(`/commande/${order.order_number}`, {
          state: {
            orderId: order.id,
            method,
            total: order.total,
            phone: form.phone,
            paymentPlan: order.payment_plan,
            deposit: order.deposit_amount,
            remaining: order.remaining_amount,
          },
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="">
        <div className="mx-auto flex max-w-7xl flex-col items-center px-6 py-32 text-center lg:px-10">
          <ShoppingBag className="h-14 w-14 text-clay" />
          <p className="mt-6 font-display text-3xl text-bark/60">Votre panier est vide</p>
          <Link to="/boutique" data-testid="checkout-empty-browse" className="mt-8 flex h-14 items-center gap-3 rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark">
            Découvrir la boutique
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <Link to="/boutique" className="mb-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-bark/50 transition-colors hover:text-terracotta">
          <ArrowLeft className="h-4 w-4" /> Continuer mes achats
        </Link>
        <Reveal y={24}>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-bark sm:text-5xl">Finaliser la commande</h1>
        </Reveal>

        <form onSubmit={submit} className="mt-12 grid gap-12 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-7">
            <Reveal delay={0.05}>
              <section>
                <h2 className="mb-2 flex items-center gap-3 font-display text-2xl font-semibold text-bark">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-terracotta font-sans text-sm font-bold text-sand">1</span>
                  Livraison à domicile
                </h2>
                <p className="mb-6 ml-11 flex items-center gap-2 text-xs font-medium text-bark/50">
                  <Home className="h-3.5 w-3.5 text-terracotta" />
                  Livraison à domicile uniquement — Dakar, Thiès, Mbour, Saint-Louis et autres villes sur devis. Pas de retrait en magasin.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="checkout-name-input" required placeholder="Nom complet *" value={form.name} onChange={set("name")} className={inputClass} />
                  <input data-testid="checkout-phone-input" required placeholder="Téléphone (ex: 77 123 45 67) *" value={form.phone} onChange={set("phone")} className={inputClass} />
                  <input data-testid="checkout-email-input" type="email" placeholder="Email (facultatif)" value={form.email} onChange={set("email")} className={`${inputClass} sm:col-span-2`} />
                  <input data-testid="checkout-address-input" required placeholder="Adresse complète (quartier, rue, repère) *" value={form.address} onChange={set("address")} className={`${inputClass} sm:col-span-2`} />
                  <select data-testid="checkout-zone-select" required value={form.zone} onChange={set("zone")} className={`${inputClass} sm:col-span-2 ${!form.zone ? "text-bark/35" : ""}`}>
                    <option value="">Ville / quartier de livraison *</option>
                    {CITIES.map((c) => (
                      <optgroup key={c} label={c}>
                        {zones.filter((z) => (z.city || "Dakar") === c).map((z) => (
                          <option key={z.id} value={z.id}>{z.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {isAutreVille && (
                    <input
                      data-testid="checkout-custom-city-input"
                      required
                      placeholder="Écrivez votre ville (ex : Kaolack, Ziguinchor, Touba…) *"
                      value={form.custom_city}
                      onChange={set("custom_city")}
                      className={`${inputClass} sm:col-span-2 border-terracotta/50`}
                    />
                  )}
                </div>

                <p className="mb-3 mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Mode de livraison — {city}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {cityOptions.map((o) => (
                    <button
                      type="button"
                      key={o.id}
                      data-testid={`delivery-option-${o.id}`}
                      onClick={() => setOptionId(o.id)}
                      className={`flex flex-col items-start gap-2 border p-5 text-left transition-all duration-300 ${
                        optionId === o.id ? "border-terracotta bg-cream shadow-[4px_4px_0_0_#A94833]" : "border-clay bg-cream/50 hover:border-terracotta/50"
                      }`}
                    >
                      <span className="flex w-full items-center justify-between">
                        <span className="font-display text-lg font-semibold text-bark">{o.name}</span>
                        <span className="rounded-full bg-terracotta px-3 py-1 text-xs font-bold text-sand">
                          {o.id === "autre_ville" ? "Tarif à confirmer" : fcfa(o.price)}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 text-xs text-bark/50">
                        <Clock className="h-3.5 w-3.5 text-terracotta" /> Délai : {o.delay}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            </Reveal>

            <Reveal delay={0.1}>
              <section>
                <h2 className="mb-6 flex items-center gap-3 font-display text-2xl font-semibold text-bark">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-terracotta font-sans text-sm font-bold text-sand">2</span>
                  Paiement
                </h2>
                <div className="grid gap-3 sm:grid-cols-3">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      data-testid={`payment-method-${m.id.replace("_", "-")}`}
                      onClick={() => {
                        setMethod(m.id);
                        if (m.id === "card") setPlan("full");
                      }}
                      className={`relative flex flex-col items-start gap-2 border p-5 text-left transition-all duration-300 ${
                        method === m.id ? "border-terracotta bg-cream shadow-[4px_4px_0_0_#A94833]" : "border-clay bg-cream/50 hover:border-terracotta/50"
                      }`}
                    >
                      <span
                        className="flex h-10 w-10 items-center justify-center rounded-full text-sand"
                        style={{ backgroundColor: m.color === "wave" ? "#1DA1F2" : m.color === "orangemoney" ? "#FF6600" : "#2C2621" }}
                      >
                        <CreditCard className="h-5 w-5" />
                      </span>
                      <span className="font-display text-lg font-semibold text-bark">{m.label}</span>
                      <span className="text-xs leading-snug text-bark/50">{m.desc}</span>
                    </button>
                  ))}
                </div>
                {method !== "card" && (
                  <div className="mt-4 space-y-2" data-testid="payment-plan-selector">
                    {[
                      { id: "full", label: "En 1 fois", detail: `${fcfa(grandTotal)} maintenant` },
                      {
                        id: "two_times",
                        label: "En 2 fois",
                        detail: `${fcfa(Math.ceil(grandTotal / 2))} maintenant, ${fcfa(Math.floor(grandTotal / 2))} à la livraison`,
                      },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`payment-plan-${p.id}`}
                        onClick={() => setPlan(p.id)}
                        className={`flex w-full flex-col gap-1 border px-5 py-3.5 text-left transition-colors duration-300 sm:flex-row sm:items-center sm:justify-between ${
                          plan === p.id ? "border-terracotta bg-cream" : "border-clay bg-white hover:border-terracotta/50"
                        }`}
                      >
                        <span className={`text-sm font-semibold ${plan === p.id ? "text-terracotta" : "text-bark"}`}>{p.label}</span>
                        <span className="text-xs text-bark/50">{p.detail}</span>
                      </button>
                    ))}
                  </div>
                )}
                {method !== "card" && (method === "wave" ? shop.wave_number : shop.orange_money_number) && (
                  <div
                    data-testid="payment-number-info"
                    className="mt-4 border border-terracotta/40 bg-cream px-5 py-4 text-sm text-bark/70"
                  >
                    Envoyez <strong className="text-bark">{fcfa(plan === "two_times" ? Math.ceil(grandTotal / 2) : grandTotal)}</strong> par{" "}
                    <strong className="text-bark">{method === "wave" ? "Wave" : "Orange Money"}</strong> au{" "}
                    <strong className="text-terracotta" data-testid="payment-number-value">
                      {method === "wave" ? shop.wave_number : shop.orange_money_number}
                    </strong>{" "}
                    puis confirmez votre commande.
                    {plan === "two_times" && (
                      <span className="mt-1 block text-xs text-bark/50">
                        Il s'agit du premier versement (50%). Le solde de {fcfa(Math.floor(grandTotal / 2))} sera payé à la livraison.
                      </span>
                    )}
                  </div>
                )}
              </section>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <aside className="border border-clay bg-cream p-8 lg:sticky lg:top-[var(--header-h,8rem)]">
                <h3 className="font-display text-2xl font-semibold text-bark">Récapitulatif</h3>
                <ul className="mt-6 divide-y divide-clay">
                  {items.map((i) => (
                    <li key={i.id} data-testid={`checkout-item-${i.id}`} className="flex items-center gap-4 py-4">
                      <div className="h-16 w-14 shrink-0 overflow-hidden border border-clay">
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <p className="font-display text-base leading-tight text-bark">{i.name}</p>
                        <p className="text-xs text-bark/45">Quantité : {i.quantity}</p>
                      </div>
                      <p className="text-sm font-bold text-bark">{fcfa(ttc(i) * i.quantity)}</p>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2 border-t border-clay pt-6 text-sm">
                  <div className="flex justify-between text-bark/60">
                    <span>Sous-total TTC — tous frais inclus</span>
                    <span data-testid="checkout-subtotal">{fcfa(total + franceFees)}</span>
                  </div>
                  <div className="flex justify-between text-bark/60">
                    <span>Livraison à domicile{selectedOption ? ` — ${selectedOption.name} (${selectedOption.delay})` : ""}</span>
                    <span data-testid="checkout-delivery-fee">
                      {deliveryFee === null ? "—" : selectedOption?.id === "autre_ville" ? "À définir" : fcfa(deliveryFee)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-clay pt-4">
                    <span className="font-display text-lg font-semibold text-bark">Total</span>
                    <span data-testid="checkout-total" className="font-display text-2xl font-bold text-terracotta">{fcfa(grandTotal)}</span>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="checkout-submit-button"
                  disabled={!canSubmit}
                  className="mt-8 flex h-14 w-full items-center justify-center gap-3 rounded-full bg-terracotta text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Traitement…
                    </>
                  ) : method === "card" ? (
                    "Payer par carte"
                  ) : (
                    "Confirmer la commande"
                  )}
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-xs text-bark/40">
                  <Lock className="h-3.5 w-3.5" /> Paiement sécurisé — vos données sont protégées
                </p>
              </aside>
            </Reveal>
          </div>
        </form>
      </div>
    </main>
  );
};

export default Checkout;
